import React, {JSX, useCallback, useEffect, useMemo, forwardRef, ReactNode} from "react";
import * as ReactDom from "react-dom";
import classNames from "classnames";
import {Spinner} from "@ok/web/components/base/Spinner/Spinner";
import {Title, titleLevels} from "../Titles/Title/Title";
import {LazyIcon} from "~/ok/components/base/Icon/LazyIcon";
import {Env} from "@ok/common/utils/env";
import {TSID} from "@ok/web/utils/TSID";
import {useGlobalModule} from "~/ok/hooks/useGlobalModule";
import classes from "./BottomSheet.styl";

export interface IBottomSheet {
    closableOnBackGround?: boolean;
    /* Не использовать, пока не реализован свайп */
    withSwipeBar?: boolean;
    title?: string;
    customTitleElement?: ReactNode;
    beforeTitleText?: () => JSX.Element;
    open?: boolean;
    isLoading?: boolean;
    redesign2025?: boolean;
    onClose?: () => void;
    children?: React.ReactNode;
    titleLevel?: typeof titleLevels[number];
    /** Компонент раскрывается на всю высоту экрана */
    fullHeight?: boolean;
    /** Компонент рисуется снизу, но не ограничен 50% экрана */
    fullHeightContainer?: boolean;
    /** Добавляет отсутпы  */
    withPadding?: boolean;
    /** Применять только стили от соответсвующего модификатора titleLevel, игнорируюя тег */
    ignoreTitleLevelTag?: boolean;
    /** Дополнительные CSS-классы для области контента. */
    contentClassName?: string;
    /** Отключить слайдер для конетнта */
    disableContentScroll?: boolean;
    /** Передаем класс, если хотим рендерить компонент в body */
    portalClassName?: string;
    /** Используем z-index: $zIndex_bottom-menu */
    isBottomMenuZIndex?: boolean;
    customStyle?: React.CSSProperties;
    isDragging?: boolean;
    isFullHeightOpen?: boolean;
    modalContentRef?: React.RefObject<HTMLDivElement>;
    /** Позволяет разблокировать скролл для основного контента (под шторкой) */
    shouldUnlockScroll?: boolean;
    onContentScroll?: () => void;
    isJustifyLeft?: boolean;
}

const portalEl = {
    __cached: null as (null | HTMLElement),
    get(className: string): HTMLElement {
        if (this.__cached !== null && this.__cached.className === className) {
            return this.__cached;
        }

        const div = document.createElement('div');
        div.id = "bottomSheet";
        div.className = className;
        document.body.appendChild(div);
        this.__cached = div;
        return div;
    }
};

export const BottomSheet = forwardRef<HTMLDivElement, IBottomSheet>(({
    withSwipeBar,
    title,
    beforeTitleText,
    isLoading,
    redesign2025,
    open = false,
    closableOnBackGround = true,
    onClose,
    children,
    titleLevel,
    fullHeight,
    fullHeightContainer,
    ignoreTitleLevelTag,
    withPadding,
    contentClassName,
    disableContentScroll,
    portalClassName,
    isBottomMenuZIndex,
    isDragging,
    isFullHeightOpen,
    customStyle,
    modalContentRef,
    onContentScroll,
    customTitleElement,
    shouldUnlockScroll = false,
    isJustifyLeft,
}, ref) => {
    const closeOnBackground = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (e.currentTarget === e.target && closableOnBackGround) {
            onClose?.();
        }
    }, [closableOnBackGround, onClose]);

    const createScrollLocker = useGlobalModule(() => window.OK.createScrollLocker).current;
    const scrollLocker = useMemo(() => createScrollLocker?.(), [createScrollLocker]);

    useEffect(() => {
        if (!scrollLocker) {
            return;
        }
        if (open && !shouldUnlockScroll) {
            scrollLocker.lock();
        } else {
            scrollLocker.unlock();
        }
    }, [open, scrollLocker, shouldUnlockScroll]);

    const faderClassName = classNames(
        classes["bottomsheet-fader"],
        {
            [classes["__open"]]: open,
            [classes["__redesign2025"]]: redesign2025,
            [classes["__full-height"]]: fullHeight,
            [classes["__full-height-container"]]: fullHeightContainer,
            [classes["__full-height-open"]]: isFullHeightOpen,
            [classes["__with-padding"]]: withPadding,
            [classes["__inside-bottom-menu"]]: isBottomMenuZIndex
        });
    const windowClassName = classNames(
        classes["bottomsheet-window"],
        {
            [classes["__open"]]: open,
            [classes["__justify-left"]]: isJustifyLeft,
        }
    );

    const renderBottomSheet = () => {
        return (
            <div className={faderClassName}
                 onClick={closeOnBackground}
            >
                <div className={windowClassName}
                     onClick={closeOnBackground}
                >
                    <div ref={ref}
                         className={classNames(classes["bottomsheet-window_block"], {[classes["__full-height"]]: fullHeight}, {[classes["__is-dragging"]]: isDragging})}
                         style={customStyle}
                    >
                        {(withSwipeBar && !redesign2025) && <span className={classes['bottomsheet-swipeBar']} /> }
                        {(withSwipeBar && redesign2025) && <span className={classes['bottomsheet-swipeBar-colored']} /> }
                        {customTitleElement}
                        {title && !customTitleElement && (
                            <Title titleLevel={titleLevel} ignoreTitleLevelTag={ignoreTitleLevelTag} className={classes["bottomsheet-title"]} textClassName={classes["bottomsheet-title_text"]}
                                   isClassicRDKView={false} delimiter={false} asideComponent={() => {
                                return (
                                    <span onClick={onClose} className={classes["bottomsheet-close"]}>
                                        <LazyIcon name="CLOSE_24" size="24" />
                                    </span>
                                );
                            }} beforeTitleText={beforeTitleText}
                            >
                                {title}
                            </Title>
                        )}
                        {isLoading
                            ? (
                                <div className={classes["bottomsheet-spinner"]}>
                                    <Spinner size="32"/>
                                </div>
                            )
                            : (
                                <div ref={modalContentRef}
                                     onScroll={onContentScroll}
                                     data-tsid={TSID.attr("bottom-sheet-content")}
                                     className={classNames(
                                     classes["bottomsheet-content"],
                                     {[classes["__disable-scroll"]]: disableContentScroll},
                                     contentClassName
                                )}>
                                    {children}
                                </div>
                            )}
                    </div>
                </div>
            </div>
        );
    };

    if (!portalClassName || Env.isServerRendering) {
        return renderBottomSheet();
    }

    return ReactDom.createPortal(renderBottomSheet(), portalEl.get(portalClassName));
});
